import Big from 'big.js';
import { isObject } from 'lodash-es';
import { logger, toBigWithDefault } from '../utils';
import { isOrderComplete } from '../utils/isOrderComplete';
import { isOrderPending } from '../utils/isOrderPending';
import type { ValueOf } from '../utils/types';
import { getEntitySubAccountNames } from './Allocation';
import type { CareNewOrderSingle } from './CareNewOrderSingle';
import type { Serializable } from './Serializable';
import {
  CptySettlementTypeEnum,
  DecisionStatusEnum,
  OrdStatusEnum,
  ParentOrderTypeEnum,
  SizeModeEnum,
  type CxlRejReasonEnum,
  type ExecTypeEnum,
  type ICareExecutionReport5203Markets,
  type OrdRejReasonEnum,
  type SideEnum,
} from './types';

export { CptySettlementTypeEnum };

export function formatCptySettlementType(value: CptySettlementTypeEnum | string | undefined): string {
  if (value == null) {
    return '';
  }

  return value === CptySettlementTypeEnum.InKind ? 'In Kind' : value;
}

export interface CareOrderCanBeNettedResult {
  result: boolean;
  failureReason?: string;
}

export interface CareOrderIsTradableResult {
  result: boolean;
  failureReason?: string;
}

export interface CareOrderIsManuallyFillableResult {
  result: boolean;
  failureReason?: string;
}

export function isCareOrder(entity: any): entity is CareOrder {
  return entity instanceof CareOrder;
}

/**
 * Checks if two care orders are compatible with each other and could be netted together
 * This is a heuristic. There is more logic on the backend. This mostly just helps the ui do some basic validation to give
 * the user some simple instant feedback
 */
export function areCareOrdersNettable(co1: CareOrder, co2: CareOrder): { nettable: boolean; failureReason?: string } {
  const { Currency: currency1, Symbol: symbol1 } = getNettableSymbolAndCurrency(co1);
  const { Currency: currency2, Symbol: symbol2 } = getNettableSymbolAndCurrency(co2);

  if (symbol1 !== symbol2) {
    return { nettable: false, failureReason: 'Cannot net care orders with different symbols' };
  }

  if (currency1 !== currency2) {
    return { nettable: false, failureReason: 'Cannot net care orders in different currencies' };
  }

  if (co1.SettleDate !== co2.SettleDate) {
    return { nettable: false, failureReason: 'Cannot net care orders with different settle dates' };
  }

  return { nettable: true };
}

/**
 * This function effectively just resolves a single-token compound to its single underlying leg's symbol and currency
 * This means that a single-token compound for an ETH fund will be nettable with a ETH-USD spot
 */
function getNettableSymbolAndCurrency(careOrder: CareOrder): Pick<CareOrder, 'Symbol' | 'Currency'> {
  const singleLegSummary = careOrder.LegSummary?.length === 1 ? careOrder.LegSummary[0] : undefined;
  if (singleLegSummary != null) {
    return {
      Symbol: singleLegSummary.Symbol,
      Currency: singleLegSummary.Currency,
    };
  }

  return {
    Symbol: careOrder.Symbol,
    Currency: careOrder.Currency,
  };
}

export class CareOrder implements Serializable<CareOrder> {
  static readonly rowID = 'OrderID';

  Timestamp: string;
  User: string;
  Symbol: string;
  /** Currency is undefined in the units case */
  Currency: string | undefined;
  OrderID: string;
  ClOrdID: string;
  OrigClOrdID?: string;
  SubmitTime: string;
  ExecID: string;
  Side: SideEnum;
  TransactTime: string;
  ExecType: ExecTypeEnum;
  OrdStatus: OrdStatusEnum;
  DecisionStatus?: DecisionStatusEnum;
  OrderQty: string;
  LeavesQty: string;
  AvailableQty: string;
  CumQty: string;
  OrdRejReason?: OrdRejReasonEnum;
  CxlRejReason?: CxlRejReasonEnum;
  RequestUser: string;
  Revision: number;
  Text?: string;
  Group: string;
  Comments: string;
  ExternalComments?: string;
  CumAmt?: string;
  AvgPx?: string;
  LastPx?: string;
  LastQty?: string;
  LastAmt?: string;
  AmountCurrency: string;
  Counterparty: string;
  LastExecID: string;
  Annotations?: CareOrderAnnotations;
  WorkingQty?: string;
  Markets?: ICareExecutionReport5203Markets[];
  ExternalGroup?: string;
  SettleDate?: string;
  CptySettlementType: CptySettlementTypeEnum;
  SubAccount?: string;

  /** Exists on CareOrder entities. Lists the WorkingOrders this CareOrder is a member in. Deprecated, exists for backwards compat reasons. */
  WorkingOrderMembership?: { OrderID: string }[];
  /** Exists on CareOrder entities. Lists the active WorkingOrder this Care Order is a member of. */
  WorkingOrderID?: string;

  /** Exists on WorkingOrder entities. List the CareOrders that are a member of this WorkingOrder. */
  MemberCareOrders?: { OrderID: string }[];

  LegSummary?: {
    Symbol: string;
    Currency: string;
    AmountCurrency: string;
    Side: SideEnum;
    LeavesQty: string;
    CumQty: string;
    AvgPx: string;
    CumAmt: string;
    LastPx: string;
    LastQty: string;
    LastAmt: string;
    OrderQty: string;
  }[];

  SizeMode?: SizeModeEnum;

  /** Sub account names grabbed from SubAccount or Allocations, whichever is populated on the entity */
  public subAccounts: string[] | undefined;

  /** If we are a WorkingOrder entity, meaning that we are the result of a netting operation */
  get isNetted() {
    return !!this.MemberCareOrders;
  }

  /** Prefixed with "my" to differentiate from normal ParentOrderType. This says "if I am a parent to someone, what kind of parent order type am I?" */
  get myParentOrderType() {
    return this.isNetted ? ParentOrderTypeEnum.WorkingOrder : ParentOrderTypeEnum.CareOrder;
  }

  get canBeNetted(): CareOrderCanBeNettedResult {
    if (this.isNetted) {
      return { result: false, failureReason: 'Netted Care Orders cannot be netted again' };
    }

    if (this.isInKind) {
      return { result: false, failureReason: 'In-kind Care Orders cannot be netted' };
    }

    if (this.OrdStatus !== OrdStatusEnum.New) {
      return { result: false, failureReason: 'Only New Care Orders can be netted' };
    }

    if (this.WorkingOrderID != null) {
      return { result: false, failureReason: 'Care Orders already assigned to a Working Order cannot be netted' };
    }

    return { result: true };
  }

  /** Netted OrderID  */
  get nettedOrderID() {
    if (this.isNetted) {
      return this.OrderID;
    } else {
      return this.WorkingOrderID;
    }
  }

  get isCancelable(): boolean {
    return !(this.isPendingOrdStatus || this.isComplete);
  }

  get isInKind() {
    return this.CptySettlementType === CptySettlementTypeEnum.InKind;
  }

  get isPendingApproval() {
    return this.isNetted && this.DecisionStatus === DecisionStatusEnum.PendingActivation;
  }

  isTradable({
    careOrderBlockDirectExecution,
  }: { careOrderBlockDirectExecution?: boolean } = {}): CareOrderIsTradableResult {
    if (careOrderBlockDirectExecution === true && !this.isNetted) {
      return { result: false, failureReason: CARE_ORDER_DIRECT_EXECUTION_BLOCKED_REASON };
    }

    if (this.isInKind) {
      return { result: false, failureReason: 'In-kind Care Orders cannot be RFQed' };
    }

    if (this.isPendingApproval) {
      return { result: false, failureReason: 'Netted Orders are not tradable until active' };
    }

    if (this.status === CareOrderStatusEnum.PendingNew) {
      return { result: false, failureReason: 'Pending New Care Orders are not tradable' };
    }

    if (this.isComplete) {
      return { result: false, failureReason: 'This care order is completed' };
    }

    if (!toBigWithDefault(this.AvailableQty, 0).gt(0)) {
      return { result: false, failureReason: 'This Care Order has no Available Quantity' };
    }

    return { result: true };
  }

  get isManuallyFillable(): CareOrderIsManuallyFillableResult {
    if (this.WorkingOrderID != null) {
      return { result: false, failureReason: 'Care orders in an active netted order cannot be manually filled' };
    }

    if (this.isPendingApproval) {
      return { result: false, failureReason: 'Netted Orders cannot be manually filled until active' };
    }

    if (
      this.OrdStatus !== OrdStatusEnum.New &&
      this.OrdStatus !== OrdStatusEnum.PartiallyFilled &&
      this.OrdStatus !== OrdStatusEnum.Filled
    ) {
      return {
        result: false,
        failureReason: 'Only New, PartiallyFilled, or Filled care orders can be manually filled',
      };
    }

    return { result: true };
  }

  get isComplete(): boolean {
    return isOrderComplete(this.OrdStatus);
  }

  get isModifiable(): boolean {
    if (this.status === CareOrderStatusEnum.PendingNew) {
      return false;
    }

    if (this.isComplete) {
      // For complete orders, we can modify them if they are specifically "Filled". Otherwise not.
      return this.OrdStatus === 'Filled';
    }

    return true;
  }

  get initiatingFrom(): string | undefined {
    if (this.Annotations != null && 'ETP' in this.Annotations && isObject(this.Annotations.ETP)) {
      return (this.Annotations as CareNewOrderSingle['Annotations'])?.ETP?.InitiatingFrom;
    }
    return undefined;
  }

  get basketOrderDetails() {
    return this.Annotations?.BasketOrder;
  }

  get fund() {
    return this.basketOrderDetails?.BasketSymbol;
  }

  get isQtyInUnits() {
    return this.SizeMode === SizeModeEnum.Units;
  }

  /** Multi-token compound orders have multiple underlying legs and cannot be sized in a single currency. */
  get isMultitoken() {
    return (this.LegSummary?.length ?? 0) > 1;
  }

  get units() {
    if (this.isQtyInUnits) {
      return this.OrderQty;
    }

    // Old code-path here. To be removed in 2.56+
    return this.basketOrderDetails?.BasketOrderQty;
  }

  get originationTrader() {
    return this.Annotations?.FIX?.OrderOriginationTrader;
  }

  get status(): ValueOf<typeof CareOrderStatusEnum> {
    const cumQty = Big(this.CumQty);
    switch (this.OrdStatus) {
      case OrdStatusEnum.PendingNew:
        return CareOrderStatusEnum.PendingNew;
      case OrdStatusEnum.New:
      case OrdStatusEnum.Replaced:
        // Netted WorkingOrders can be accepted as FIX New while still awaiting second-trader approval.
        if (this.isPendingApproval) {
          return CareOrderStatusEnum.PendingApproval;
        }
        if (toBigWithDefault(this.WorkingQty, 0).gt(0)) {
          return CareOrderStatusEnum.Working;
        }
        return CareOrderStatusEnum.New;
      case OrdStatusEnum.PendingCancel:
        return CareOrderStatusEnum.PendingCancel;
      case OrdStatusEnum.PendingReplace:
        return CareOrderStatusEnum.PendingReplace;
      case OrdStatusEnum.PartiallyFilled:
        return CareOrderStatusEnum.PartiallyFilled;
      case OrdStatusEnum.Filled:
        if (toBigWithDefault(this.AvailableQty, 0).gt(0)) {
          // [MKTP-1575] - if a CO's child is force canceled from PendingFix, the CO goes to "Filled" (terminal), but still has AvailableQty, which is a bad state.
          // We show this as FailedFill (more correct), and we will also help the user understand that this CO should be force-canceled, too.
          return CareOrderStatusEnum.FailedFill;
        }
        return CareOrderStatusEnum.Filled;
      case OrdStatusEnum.DoneForDay:
        if (cumQty.eq(0)) {
          return CareOrderStatusEnum.Canceled;
        }
        return cumQty.gte(this.OrderQty) ? CareOrderStatusEnum.Filled : CareOrderStatusEnum.FilledAndCanceled;
      case OrdStatusEnum.Canceled:
        return CareOrderStatusEnum.Canceled;
      case OrdStatusEnum.Rejected:
        return CareOrderStatusEnum.Rejected;
      case OrdStatusEnum.Holding:
      case OrdStatusEnum.PendingFix:
        logger.error(new Error(`Unexpected OrdStatus: ${this.OrdStatus}`));
        return CareOrderStatusEnum.Unknown;
      default: {
        const _exhaustiveCheck: never = this.OrdStatus;
        logger.error(new Error(`Unexpected OrdStatus: ${this.OrdStatus}`));
        return CareOrderStatusEnum.Unknown;
      }
    }
  }

  toJSON() {
    return { ...this };
  }

  // Private for now to encourage using the more abstract getters (isCancelable etc.)
  private get isPendingOrdStatus(): boolean {
    return isOrderPending(this.OrdStatus);
  }

  /**
   * Separating the namespace out here for the use case where we are combining several Order-types in one blotter.
   * On a CareOrder, the Markets property describes what Markets are approved for trading on. Undefined / empty means all.
   */
  get approvedMarkets() {
    return this.Markets?.map(m => m.Market);
  }

  get isSettleDateModifiable() {
    if (this.OrdStatus === 'New' || this.OrdStatus === 'PartiallyFilled') {
      // We can edit the settlement date if 1) there are no "working (child) orders", and 2) there are no fills. As in - nothing has happened yet.
      // The reason that PartiallyFilled is here is because you can get a partial fill and force-cancel it, at which point we need to regard the CO as
      // "nothing happened yet".
      return toBigWithDefault(this.WorkingQty, 0).eq(0) && toBigWithDefault(this.CumQty, 0).eq(0);
    }

    return false;
  }

  constructor(data: CareOrder | ICareOrder) {
    this.Timestamp = data.Timestamp;
    this.User = data.User;
    this.Symbol = data.Symbol;
    this.Currency = data.Currency;
    this.OrderID = data.OrderID;
    this.ClOrdID = data.ClOrdID;
    this.OrigClOrdID = data.OrigClOrdID;
    this.SubmitTime = data.SubmitTime;
    this.ExecID = data.ExecID;
    this.Side = data.Side;
    this.TransactTime = data.TransactTime;
    this.ExecType = data.ExecType;
    this.OrdStatus = data.OrdStatus;
    this.DecisionStatus = data.DecisionStatus;
    this.OrderQty = data.OrderQty ?? '0';
    this.LeavesQty = data.LeavesQty ?? '0';
    this.CumQty = data.CumQty ?? '0';
    this.OrdRejReason = data.OrdRejReason;
    this.CxlRejReason = data.CxlRejReason;
    this.RequestUser = data.RequestUser;
    this.Revision = data.Revision;
    this.Text = data.Text;
    this.Group = data.Group;
    this.Comments = data.Comments;
    this.ExternalComments = data.ExternalComments;
    this.AmountCurrency = data.AmountCurrency;
    this.Counterparty = data.Counterparty;
    this.CumAmt = data.CumAmt;
    this.AvgPx = data.AvgPx;
    this.LastExecID = data.LastExecID;
    this.LastPx = data.LastPx;
    this.LastQty = data.LastQty;
    this.LastAmt = data.LastAmt;
    this.LastExecID = data.LastExecID;
    this.Annotations = data.Annotations;
    this.ExternalGroup = data.ExternalGroup;
    this.SettleDate = data.SettleDate;
    this.CptySettlementType = data.CptySettlementType ?? CptySettlementTypeEnum.Cash;
    this.Markets = data.Markets;
    this.AvailableQty = data.AvailableQty ?? '0';
    this.SubAccount = data.SubAccount;
    this.LegSummary = data.LegSummary;
    this.SizeMode = data.SizeMode;
    this.MemberCareOrders = data.MemberCareOrders;
    this.WorkingOrderMembership = data.WorkingOrderMembership;
    // For backwards compat reasons for a short time. Prefer WorkingOrderID, fallback to old backwards-compat WorkingOrderMembership group entry [0].
    this.WorkingOrderID = data.WorkingOrderID ?? data.WorkingOrderMembership?.at(0)?.OrderID;
    this.subAccounts = getEntitySubAccountNames(data);

    try {
      if (this.isComplete) {
        this.WorkingQty = '0';
      } else if (this.OrdStatus === OrdStatusEnum.PendingNew || this.isPendingApproval) {
        // PendingNew orders and pre-approval WorkingOrders can report LeavesQty while AvailableQty is still 0; they have not been accepted for trading yet.
        this.WorkingQty = '0';
      } else {
        this.WorkingQty = Big(this.OrderQty).minus(this.CumQty).minus(this.AvailableQty).toFixed();
      }
    } catch (e) {
      logger.error(e as Error);
      this.WorkingQty = '0';
    }
  }
}

export const CARE_ORDER_DIRECT_EXECUTION_BLOCKED_REASON = 'Only Netted Care Orders can be traded.';

export interface CareOrderAnnotations {
  BasketOrder?: BasketOrderAnnotation;
  ETP?: {
    InitiatingFrom?: string;
  };
  FIX?: {
    OrderOriginationTrader?: string;
  };
  SettleOffset?: string;
}

export interface BasketOrderAnnotation {
  BasketSymbol: string;
  BasketOrderQty?: string;
  BasketRatio?: string;
  BasketRatioDate?: string;
}

export const CareOrderStatusEnum = {
  PendingNew: 'PendingNew',
  PendingApproval: 'PendingApproval',
  New: 'New',
  Working: 'Working',
  Rejected: 'Rejected',
  Canceled: 'Canceled',
  PartiallyFilled: 'PartiallyFilled',
  Filled: 'Filled',
  FilledAndCanceled: 'FilledAndCanceled',
  FailedFill: 'FailedFill',
  PendingCancel: 'PendingCancel',
  PendingReplace: 'PendingReplace',
  Unknown: 'Unknown',
} as const;
export type CareOrderStatusEnum = (typeof CareOrderStatusEnum)[keyof typeof CareOrderStatusEnum];

type CareOrderGetters =
  | 'isPendingApproval'
  | 'isCancelable'
  | 'isComplete'
  | 'initiatingFrom'
  | 'status'
  | 'toJSON'
  | 'basketOrderDetails'
  | 'units'
  | 'fund'
  | 'isModifiable'
  | 'approvedMarkets'
  | 'originationTrader'
  | 'isTradable'
  | 'isManuallyFillable'
  | 'subAccounts'
  | 'isSettleDateModifiable'
  | 'isNetted'
  | 'myParentOrderType'
  | 'canBeNetted'
  | 'isQtyInUnits'
  | 'isInKind'
  | 'isMultitoken'
  | 'nettedOrderID';

export type ICareOrder = Omit<CareOrder, CareOrderGetters>;
